import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { Loader } from '../../../components/loader/SpinnerLogo';
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import useFetch from '../../../hooks/useFetch';
import { RootState } from '../../../redux/store';
import { setSelectedView } from '../../../redux/parkProfileSlice';
import { menuRoutes } from "../../../router/menu";
import { UserApiResponse } from '../../../type/entities/user-type';
import { ParkService } from '../../../services/park/parkService';
import { FaSkull, FaWind } from 'react-icons/fa';
import { FaRegFileLines, FaTriangleExclamation } from 'react-icons/fa6';
import { TbClockOff } from 'react-icons/tb';
import { LuClipboardList } from 'react-icons/lu';
import CustomSpinner from '../../../loader/CustomSpinner';
import { ActionIcon, Tooltip } from 'rizzui';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import DeletePopover from '../../../components/buttons/DeletePopover';
import ParkFormModal from '../components/ParkFormModal';
import { toast } from 'sonner';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { PiBinoculars } from 'react-icons/pi';

const menuItems: MenuItem[] = [
    {
        label: 'Aerogeneradores',
        path: menuRoutes.parks.profile.windTurbines,
        icon: <FaWind/>,
        permission: {
            group: 'windturbine',
            action: 'list',
        },
    },
    {
        label: 'Documentos',
        path: menuRoutes.parks.profile.documents,
        icon: <FaRegFileLines/>,
        permission: {
            group: 'document',
            action: 'list',
        },
    },
    {
        label: 'Incidencias',
        path: menuRoutes.parks.profile.issues,
        icon: <FaTriangleExclamation/>,
        permission: {
            group: 'issues',
            action: 'list',
        },
    },
    {
        label: 'Carroñas',
        path: menuRoutes.parks.profile.carrion,
        icon: <FaSkull/>,
        permission: {
            group: 'carrion',
            action: 'list',
        },
    },
    {
        label: 'Paradas',
        path: menuRoutes.parks.profile.stops,
        icon: <TbClockOff/>,
        permission: {
            group: 'preventive_stops',
            action: 'list',
        },
    },
    {
        label: 'Censos',
        path: menuRoutes.parks.profile.census,
        icon: <LuClipboardList/>,
        permission: {
            group: 'census',
            action: 'list',
        },
    },
    {
        label: 'OFC',
        path: menuRoutes.parks.profile.ofc,
        icon: <PiBinoculars/>,
        permission: {
            group: 'census',
            action: 'list',
        },
    },
];

export default function ParkProfileLayout({ children, isLoading }: { children?: React.ReactNode, isLoading?: boolean }) {

    // STATES

    const service = new ParkService();
    const [isOpenModalForm, setIsOpenModalForm] = useState<boolean>(false);

    // HOOKS

    const { selectedView } = useSelector((state: RootState) => state.parkProfile);
    const { id = '' } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { handleErrors } = useHandleErrors();

    // DATA FETCHING

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getParkById(id);
        return response.getResponseData() as UserApiResponse;
    }, [id]));

    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA EL PARQUE ACTUAL
     * @EN DELETES THE CURRENT PARK
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const handleDelete = async () => {
        try {
            const response = await (await service.deletePark(id)).getResponseData();
            if(response.success) {
                toast.success("Parque eliminado correctamente");
                navigate(menuRoutes.parks.path);
            } else {
                handleErrors(response);
            }
        } catch (error: any) {
            handleErrors(error);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CIERRA EL MODAL DE EDICIÓN
     * @EN CLOSES THE EDIT MODAL
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const onCloseModal = () => {
        setIsOpenModalForm(false);
    };
    //-----------------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        navigate(`${menuRoutes.parks.path}/${id}/profile/${selectedView}`);
    }, [selectedView]);

    // RENDER

    return (
        <>
            {data ? ( <>

            <div className='h-[200px] bg-white dark:bg-gray-50 shadow-sm rounded-md p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-4 sticky z-[70] mt-3'>

                <div className='col-span-3 flex flex-col gap-2'>

                    <div className='flex justify-between items-start'>
                        <div className='flex flex-col gap-2'>
                            <h2 className='text-[20px] font-bold capitalize'>   
                                {data?.name} ({data?.alias})
                            </h2>

                            <span className='text-sm font-semibold text-gray-500 dark:text-gray-400'>
                                {data?.location}
                            </span>
                        </div>
                        
                        {/* Action buttons */}
                        <div className="flex items-center gap-2">
                            <Tooltip size="sm" content="Editar parque" placement="top">
                                <ActionIcon
                                    size="sm"
                                    variant="outline"
                                    className="hover:!border-gray-400 hover:text-gray-700"
                                    onClick={() => setIsOpenModalForm(true)}
                                >
                                    <MdOutlineModeEditOutline className="h-4 w-4" />
                                </ActionIcon>
                            </Tooltip>
                            
                            <DeletePopover
                                title="¿Eliminar parque?"
                                description={`¿Estás seguro de que deseas eliminar el parque "${data?.name}"? Esta acción no se puede deshacer.`}
                                onDelete={handleDelete}
                            />
                        </div>
                    </div>

                </div>

                <div className='text-sm'>
                    Capacidad:
                    <div className='text-sm font-semibold text-gray-500 dark:text-gray-400'>
                        {data?.capacity} MW
                    </div>
                </div>

                <div className='text-sm'>
                    Fecha de inicio: 
                    <div className='text-sm font-semibold text-gray-500 dark:text-gray-400'>
                        {data?.createdAt ? new Date(data?.createdAt?.date).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                        }) : 'Sin fecha'}   
                    </div>
                </div>

                <div className='text-sm'>
                    <span className='mb-1'>Estado:</span>
                    <div className='text-sm font-semibold text-gray-500 dark:text-gray-400 mt-1'>
                        <span
                            className={'text-sm rounded  p-1 text-white' + (data?.status ? ' bg-green-500' : ' bg-red-500')}
                        >
                        {data?.status ? 'Activo' : 'Inactivo'}
                        </span>
                    </div>
                </div>

                <div className='text-sm'>
                    Cliente:
                    <div className='text-sm font-semibold text-gray-500 dark:text-gray-400'>
                        <a href={`/clients/${data?.client?.id}/profile/info`} className='text-primary hover:text-secondary'>
                            {data?.client && data?.client.name}
                        </a>
                    </div>
                </div>
                <div className='text-sm'>
                    Término municipal:
                    <div className='text-sm font-semibold text-gray-500 dark:text-gray-400'>
                        {data?.municipal}
                    </div>
                </div>
                
            </div>

            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {isLoading ? <Loader height='60vh' /> : <><Outlet /></>}

            {/* Park Form Modal */}
             <ParkFormModal
                 isOpen={isOpenModalForm}
                 onClose={onCloseModal}
                 park={data}
                 refetch={refetch}
             />

            </>
            ) : (
                <div className='flex flex-col items-center justify-center'>
                    <CustomSpinner />
                </div>
            )}
        </>
    );
}